<template>
  <v-layout>
    <v-flex>
      <v-card>
        <v-card-title>
          {{ updateMode ? $t('docSignerUpdate') : $t('createDocSigner') }}
        </v-card-title>
        <v-list-item>
          <v-list-item-content>
            <v-row v-if="!updateMode && isMOBILSIGN" align="center">
              <v-col cols="6" md="2">
                <v-switch
                  v-model="isMobileSignMode"
                  class="mx-3"
                  :label="$t('mobilSignMode')"
                ></v-switch>
              </v-col>
              <v-col v-if="isMobileSignMode" cols="6" md="3">
                <v-select
                  v-model="mobilSignMode"
                  class="mx-3"
                  :items="mobilSignModeList"
                  :label="$t('select') +' ' + $t('mobilSignMode')+`*`"
                  :rules="[rules.required]"
                  chips
                ></v-select>
              </v-col>
            </v-row>

            <v-switch
              v-model="setAdvancedJson"
              class="mx-3"
              :label="$t('advancedJson')"
            ></v-switch>
            <v-form v-if="setAdvancedJson" ref="form" v-model="formValid">
              <v-row align="center">
                <v-col cols="6" md="2">
                  <input
                    ref="jsonUpload"
                    style="display: none"
                    type="file"
                    accept=".json"
                    @change="loadDocSignerJsonDataFromFile($event)"
                  >
                  <v-btn class="green" @click="$refs.jsonUpload.click()">
                    <v-icon left>mdi-upload</v-icon>
                    {{ !!docSignerJsonData ? $t('replace') : $t('upload') }} json {{ $t('file') }}
                  </v-btn>
                </v-col>
                <v-col>
                  <v-textarea
                    v-model="docSignerJsonData"
                    :label="$t('advancedJson')"
                    :rules="[rules.required]"
                    auto-grow
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>

            <v-form v-else ref="form" v-model="formValid">
              <div v-if="!isMobileSignMode">
                <v-text-field
                  id="8a4a3c28"
                  v-model="simpleDocSignerDto.name"
                  :label="$t('name')+`*`"
                  :counter="maxCharacters"
                  :rules="[rules.required, rules.minMax]"
                  :disabled="updateMode"
                ></v-text-field>
                <div v-if="!updateMode" class="text-center d-flex align-center">
                  <v-text-field
                    id="6160bcc5"
                    v-model="simpleDocSignerDto.subjectDn"
                    :label="$t('subjectDn')+`*`"
                    :counter="maxCharacters"
                    :rules="[rules.required, rules.minMax]"
                    :disabled="updateMode"
                  ></v-text-field>
                  <v-tooltip v-if="!updateMode" top>
                    <template v-slot:activator="{ on }">
                      <v-icon color="primary" v-on="on">mdi-help-circle</v-icon>
                    </template>
                    <span>{{ $t('helper.subjectDn') }}</span>
                  </v-tooltip>
                </div>
                <v-row align="center">
                  <v-col cols="6" md="4">
                    <v-select
                      id="49152875"
                      v-model="simpleDocSignerDto.keyStoreName"
                      class="mx-3"
                      :items="keyStoreNameList"
                      :label="$t('select') +' ' + $t('keyStoreName')+`*`"
                      :rules="[rules.required]"
                      chips
                      :disabled="updateMode"
                      @change="keyStoreNameChanged"
                    ></v-select>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-select
                      id="9e10a340"
                      v-model="simpleDocSignerDto.sigParams.digestAlgorithm"
                      class="mx-3"
                      :items="digestAlgorithmList"
                      :label="$t('select') +' ' + $t('digestAlgorithm')"
                      chips
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row v-if="!updateMode" align="center">
                  <v-col>
                    <v-radio-group
                      id="278620f8"
                      v-model="keyMethod"
                      class="mx-3"
                      row
                      @change="changeVisibleKeyMethodPlatform"
                    >
                      <v-radio
                        :label="$t('keyMethod.generate')"
                        :value="keyMethodList[0]"
                      ></v-radio>
                      <v-radio
                        :label="$t('keyMethod.import')"
                        :value="keyMethodList[1]"
                      ></v-radio>
                      <v-radio
                        :label="$t('keyMethod.exist')"
                        :value="keyMethodList[2]"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>
                <v-row v-if="keyGenerationPlatform" align="center">
                  <v-col cols="6" md="2">
                    <v-select
                      id="d910f2ee"
                      v-model="simpleDocSignerDto.generateKey.keyAlg"
                      class="mx-3"
                      :items="keyAlgList"
                      :label="$t('select') +' ' + $t('keyAlg')"
                      chips
                      :disabled="updateMode"
                      @change="changeDefaultValuesByKeyAlg"
                    ></v-select>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-select
                      id="deb8b9a9"
                      v-model="simpleDocSignerDto.generateKey.keySize"
                      class="mx-3"
                      :items="simpleDocSignerDto.generateKey.keyAlg === keyAlgList[1] ? ecKeyNameList : rsaKeySizeList"
                      :label="$t('select') +' ' + $t('keySize')"
                      chips
                      :disabled="updateMode"
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row v-if="keyImportPlatform">
                  <v-col cols="6" md="1">
                    <input
                      id="8c957afb"
                      ref="keyUpload"
                      style="display: none"
                      type="file"
                      @change="loadKeyFromFile($event)"
                    >
                    <v-btn class="green" @click="$refs.keyUpload.click()">
                      <v-icon left>mdi-upload</v-icon>
                      {{ !!simpleDocSignerDto.importKey.pem ? $t('replace') : $t('upload') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="6" md="4">
                    <v-text-field
                      id="3b999539"
                      v-model="simpleDocSignerDto.importKey.password"
                      :label="$t('keyPassword')"
                      type="password"
                      :rules="[rules.required]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row v-if="existingKeyPlatform">
                  <v-col>
                    <v-select
                      id="53a69fcd"
                      v-model="simpleDocSignerDto.keyAlias"
                      class="mx-3"
                      :items="keyAliasList"
                      item-text="text"
                      item-value="value"
                      :label="$t('select') +' ' + $t('keyAlias')+`*`"
                      :rules="[rules.required]"
                      chips
                      :disabled="updateMode"
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row align="center">
                  <v-col cols="6" md="4">
                    <v-select
                      id="03c536ef"
                      v-model="signatureForm"
                      class="mx-3"
                      :items="signatureFormList"
                      :label="$t('select') +' ' + $t('signatureForm')+`*`"
                      :rules="[rules.required]"
                      chips
                      @change="creatingSignatureLevelListBySignatureForm"
                    ></v-select>
                  </v-col>
                  <v-col v-if="!isCodeSigningFormat" cols="6" md="2">
                    <v-select
                      id="f76a43c4"
                      v-model="simpleDocSignerDto.sigParams.signatureLevel"
                      class="mx-3"
                      :items="signatureLevelList"
                      :label="$t('select') +' ' + $t('signatureLevel')+`*`"
                      :rules="[rules.required]"
                      chips
                    ></v-select>
                  </v-col>
                  <v-col v-if="isCodeSigningGPGFormat" cols="6" md="2">
                    <v-checkbox
                      id="03c536ew"
                      v-model="signatureCodeSigningDto.isPGPDetached"
                      class="mx-3"
                      :label="$t('isPGPDetached')"
                    ></v-checkbox>
                  </v-col>
                  <v-col v-if="!isCodeSigningFormat && (!useXmlTemplate && !useDosszieMode)" cols="6" md="2">
                    <v-select
                      id="0fd451ea"
                      v-model="simpleDocSignerDto.sigParams.signaturePackaging"
                      class="mx-3"
                      :items="signaturePackagingList"
                      :label="$t('select') +' ' + $t('signaturePackaging')"
                      :rules="[rules.required]"
                      chips
                    ></v-select>
                  </v-col>
                  <v-col v-if="!isCodeSigningFormat && (!useXmlTemplate && !useDosszieMode)" cols="6" md="2">
                    <v-select
                      v-model="simpleDocSignerDto.sigParams.asicContainerType"
                      class="mx-3"
                      :items="asicContainerTypeList"
                      :label="$t('select') +' ' + $t('asicContainerType')"
                      chips
                    ></v-select>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-checkbox
                      v-for="maskGenerationFunction in maskGenerationFunctionList"
                      :key="maskGenerationFunction"
                      v-model="simpleDocSignerDto.sigParams.maskGenerationFunction"
                      :label="$t('maskGenerationFunction')"
                      :value="maskGenerationFunction"
                    ></v-checkbox>
                  </v-col>
                </v-row>

                <!-- signatureImageParameters -->
                <v-row v-if="!isCodeSigningFormat && (!useXmlTemplate && !useDosszieMode)" align="center">
                  <v-col cols="6" md="3">
                    <v-switch
                      v-model="setSignatureImageParameters"
                      class="mx-3"
                      :label="$t('signatureImageParameters')"
                      @change="setSignatureImageDataUrlToData"
                    ></v-switch>
                  </v-col>
                  <v-row v-if="setSignatureImageParameters" align="center">
                    <v-col cols="6" md="1">
                      <v-text-field
                        v-model="signatureImageDto.page"
                        type="number"
                        :label="$t('page')"
                        min="1"
                        oninput="validity.valid||(value='')"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </v-col>
                    <!-- the origin is the left and top corner of the page -->
                    <v-col cols="6" md="1">
                      <v-text-field
                        v-model="signatureImageDto.xAxis"
                        type="number"
                        :label="'x'+$t('axis')"
                        min="0"
                        oninput="validity.valid||(value='')"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" md="1">
                      <v-text-field
                        v-model="signatureImageDto.yAxis"
                        type="number"
                        :label="'y'+$t('axis')"
                        min="0"
                        oninput="validity.valid||(value='')"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" md="2">
                      <v-text-field
                        v-model="signatureImageDto.width"
                        type="number"
                        :label="$t('width')"
                        :placeholder="$t('placeholder.notSpecified')"
                        min="0"
                        oninput="validity.valid||(value='')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" md="2">
                      <v-text-field
                        v-model="signatureImageDto.height"
                        type="number"
                        :label="$t('height')"
                        :placeholder="$t('placeholder.notSpecified')"
                        min="0"
                        oninput="validity.valid||(value='')"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-row>
                <v-row v-if="setSignatureImageParameters" align="center">
                  <v-col cols="6" md="1">
                    <input
                      ref="signatureImageUpload"
                      style="display: none"
                      type="file"
                      accept="image/*"
                      @change="loadSignatureImageFromFile($event)"
                    >
                    <v-btn class="green" @click="$refs.signatureImageUpload.click()">
                      <v-icon left>mdi-upload</v-icon>
                      {{ !!simpleDocSignerDto.sigImgDataUrl ? $t('replace') : $t('upload') }}
                    </v-btn>
                  </v-col>
                  <v-col v-if="!!simpleDocSignerDto.sigImgDataUrl" cols="6" md="2">
                    <div class="imagePreviewWrapper">
                      <v-img :src="simpleDocSignerDto.sigImgDataUrl" />
                    </div>
                  </v-col>
                </v-row>

                <!-- signatureImageTextParameters -->
                <v-row v-if="!isCodeSigningFormat && (!useXmlTemplate && !useDosszieMode)" align="center">
                  <v-col cols="6" md="3">
                    <v-switch
                      v-model="setSignatureImageTextParameters"
                      class="mx-3"
                      :label="$t('signatureImageTextParameters')"
                    ></v-switch>
                  </v-col>
                  <v-row v-if="setSignatureImageTextParameters" align="center">
                    <v-col cols="6" md="4">
                      <v-text-field
                        v-model="signatureTextDto.text"
                        :label="$t('text')+`*`"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" md="2">
                      <v-select
                        v-model="signatureTextDto.signerTextPosition"
                        class="mx-3"
                        :items="signerTextPositionList"
                        :label="$t('select') + ' ' + $t('text') + ' ' + $t('position') +`*`"
                        :rules="[rules.required]"
                        chips
                      ></v-select>
                    </v-col>
                    <!-- Black RGB color code: RED=0, GREEN=0, BLUE=0 -->
                    <v-col cols="6" md="1">
                      <v-text-field
                        v-model="signatureTextDto.textColor.red"
                        type="number"
                        :label="$t('red')"
                        min="0"
                        max="255"
                        :rules="[rules.required, rules.numberFromToValid]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" md="1">
                      <v-text-field
                        v-model="signatureTextDto.textColor.green"
                        type="number"
                        :label="$t('green')"
                        min="0"
                        max="255"
                        :rules="[rules.required, rules.numberFromToValid]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" md="1">
                      <v-text-field
                        v-model="signatureTextDto.textColor.blue"
                        type="number"
                        :label="$t('blue')"
                        min="0"
                        max="255"
                        :rules="[rules.required, rules.numberFromToValid]"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-row>

                <!-- setTimestampUrl -->
                <v-row align="center">
                  <v-col cols="6" md="2">
                    <v-switch
                      v-model="setTimestampUrl"
                      class="mx-3"
                      :label="$t('timestampUrl')"
                      @change="setTimestampUrlToData"
                    ></v-switch>
                  </v-col>
                  <v-col v-if="setTimestampUrl" cols="6" md="6">
                    <v-text-field
                      v-model="simpleDocSignerDto.timestampUrl"
                      :label="$t('timestampUrl')"
                      :counter="maxCharacters"
                      :rules="[rules.required, rules.minMax]"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- generateGPGCertificatedPublicKey -->
                <div v-if="updateMode && isCodeSigningFormat" class="mx-2">
                  <v-text-field
                    id="643e30f0"
                    v-model="signatureCodeSigningDto.gpgPublicKeyDN"
                    :label="$t('GPGPublicKeyDN')"
                  ></v-text-field>
                  <v-textarea
                    id="4ce90f2c"
                    v-model="signatureCodeSigningDto.gpgPublicKey"
                    auto-grow
                    rows="2"
                    :label="$t('generateGPGCertificatedPublicKeyLabel')"
                  ></v-textarea>

                  <v-btn
                    id="a78d1f5a"
                    class="primary mx-2 my-2"
                    @click="generateGPGCertificatedPublicKey"
                  >
                    <v-icon left>mdi-upload</v-icon>
                    {{ $t('generateGPGCertificatedPublicKey') }}
                  </v-btn>
                </div>

                <!-- downloadCertReq -->
                <v-btn
                  v-if="updateMode"
                  id="fa1d51cc"
                  class="primary mx-2 my-2"
                  @click="downloadCertReqFile(simpleDocSignerDto.certReq.trim())"
                >
                  <v-icon left>mdi-download</v-icon>
                  {{ $t('download') +' '+ $t('certReq') }}
                </v-btn>

                <!-- loadSignerCertificate -->
                <div v-if="updateMode" class="mx-2">
                  <input
                    id="39bca128"
                    ref="signerCertificateUpload"
                    style="display: none"
                    type="file"
                    accept=".pem, .cer"
                    @change="loadSignerCertificateFromFile($event)"
                  >
                  <v-btn class="green" @click="$refs.signerCertificateUpload.click()">
                    <v-icon left>mdi-upload</v-icon>
                    {{ !!simpleDocSignerDto.signerCert ? $t('replace') : $t('upload') }} {{ $t('signerCert') }}
                  </v-btn>
                </div>

                <!-- signerCert -->
                <v-row v-if="updateMode && !!simpleDocSignerDto.signerCert" id="8bbcf440">
                  <v-col>
                    <v-card class="mx-2" raised>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle class="subtitle-2 text-left">
                            {{ $t('signerCert') }}
                          </v-list-item-subtitle>
                          <v-text-field
                            v-model="simpleDocSignerDto.signerCert.serialNumber"
                            :label="$t('serialNumber')"
                            :readonly="!!simpleDocSignerDto.signerCert"
                          ></v-text-field>
                          <v-text-field
                            v-model="simpleDocSignerDto.signerCert.issuer"
                            :label="$t('issuer')"
                            :readonly="!!simpleDocSignerDto.signerCert"
                          ></v-text-field>
                          <v-text-field
                            v-model="simpleDocSignerDto.signerCert.subject"
                            :label="$t('subject')"
                            :readonly="!!docSigner.signerCert"
                          ></v-text-field>
                          <v-row>
                            <v-col cols="6">
                              <v-text-field
                                :value="convertDate(simpleDocSignerDto.signerCert.notBefore)"
                                :label="$t('notBefore')"
                                :readonly="!!simpleDocSignerDto.signerCert"
                              ></v-text-field>
                            </v-col>
                            <v-col cols="6">
                              <v-text-field
                                :value="convertDate(simpleDocSignerDto.signerCert.notAfter)"
                                :label="$t('notAfter')"
                                :readonly="!!simpleDocSignerDto.signerCert"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-list-item-content>
                      </v-list-item>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- loadAdditionalCertificate -->
                <div v-if="updateMode" class="mx-2 my-2">
                  <input
                    id="29600413"
                    ref="additionalCertificateUpload"
                    style="display: none"
                    type="file"
                    accept=".pem, .cer"
                    @change="loadAdditionalCertificateFromFile($event)"
                  >
                  <v-btn class="green" @click="$refs.additionalCertificateUpload.click()">
                    <v-icon left>mdi-upload</v-icon>
                    {{ $t('upload') + ' ' + $t('additionalCert') }}
                  </v-btn>
                </div>

                <!-- additionalCert(s) -->
                <v-row v-if="updateMode" id="5e7ef11c">
                  <v-col v-for="(cert, index) in simpleDocSignerDto.additionalCerts" :key="index" cols="12">
                    <v-card class="mx-2" raised>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle class="subtitle-2 text-left">
                            {{ $t('additionalCert') + ' - ' + (index + 1) }}
                          </v-list-item-subtitle>
                          <v-text-field
                            v-model="simpleDocSignerDto.additionalCerts[index].serialNumber"
                            :label="$t('serialNumber')" :readonly="!!simpleDocSignerDto.additionalCerts[index]"
                          ></v-text-field>
                          <v-text-field
                            v-model="simpleDocSignerDto.additionalCerts[index].issuer"
                            :label="$t('issuer')"
                            :readonly="!!simpleDocSignerDto.additionalCerts[index]"
                          ></v-text-field>
                          <v-text-field
                            v-model="simpleDocSignerDto.additionalCerts[index].subject"
                            :label="$t('subject')"
                            :readonly="!!simpleDocSignerDto.additionalCerts[index]"
                          ></v-text-field>
                          <v-row>
                            <v-col cols="6">
                              <v-text-field
                                :value="convertDate(simpleDocSignerDto.additionalCerts[index].notBefore)"
                                :label="$t('notBefore')"
                                :readonly="!!simpleDocSignerDto.additionalCerts[index]"
                              ></v-text-field>
                            </v-col>
                            <v-col cols="6">
                              <v-text-field
                                :value="convertDate(simpleDocSignerDto.additionalCerts[index].notAfter)"
                                :label="$t('notAfter')"
                                :readonly="!!simpleDocSignerDto.additionalCerts[index]"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-list-item-content>
                      </v-list-item>
                      <v-card-actions>
                        <v-btn color="error" @click="removeAdditionalCert(index)">{{ $t('delete') }}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <div v-if="isMobileSignSignMode">
                <v-text-field
                  id="241f13e3"
                  v-model="simpleDocSignerDto.name"
                  :label="$t('name')+`*`"
                  :counter="maxCharacters"
                  :rules="[rules.required, rules.minMax]"
                  :disabled="updateMode"
                ></v-text-field>
                <div v-if="!updateMode" class="text-center d-flex align-center">
                  <v-text-field
                    id="43fa9aaa"
                    v-model="simpleDocSignerDto.subjectDn"
                    :label="$t('subjectDn')+`*`"
                    :counter="maxCharacters"
                    :rules="[rules.required, rules.minMax]"
                    :disabled="updateMode"
                  ></v-text-field>
                  <v-tooltip v-if="!updateMode" top>
                    <template v-slot:activator="{ on }">
                      <v-icon color="primary" v-on="on">mdi-help-circle</v-icon>
                    </template>
                    <span>{{ $t('helper.subjectDn') }}</span>
                  </v-tooltip>
                </div>
                <v-row align="center">
                  <v-col cols="6" md="4">
                    <v-select
                      id="18202f9f"
                      v-model="simpleDocSignerDto.keyStoreName"
                      class="mx-3"
                      :items="keyStoreNameList"
                      :label="$t('select') +' ' + $t('keyStoreName')+`*`"
                      :rules="[rules.required]"
                      chips
                      :disabled="updateMode"
                      @change="keyStoreNameChanged"
                    ></v-select>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-select
                      id="461aaf65"
                      v-model="simpleDocSignerDto.sigParams.digestAlgorithm"
                      class="mx-3"
                      :items="digestAlgorithmList"
                      :label="$t('select') +' ' + $t('digestAlgorithm')"
                      chips
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row v-if="!updateMode" align="center">
                  <v-col>
                    <v-radio-group
                      id="b5231332"
                      v-model="keyMethod"
                      class="mx-3"
                      row
                      @change="changeVisibleKeyMethodPlatform"
                    >
                      <v-radio
                        :label="$t('keyMethod.generate')"
                        :value="keyMethodList[0]"
                      ></v-radio>
                      <v-radio
                        :label="$t('keyMethod.import')"
                        :value="keyMethodList[1]"
                      ></v-radio>
                      <v-radio
                        :label="$t('keyMethod.exist')"
                        :value="keyMethodList[2]"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>
                <v-row v-if="keyGenerationPlatform" align="center">
                  <v-col cols="6" md="2">
                    <v-select
                      id="4f3caed1"
                      v-model="simpleDocSignerDto.generateKey.keyAlg"
                      class="mx-3"
                      :items="keyAlgList"
                      :label="$t('select') +' ' + $t('keyAlg')"
                      chips
                      :disabled="updateMode"
                      @change="changeDefaultValuesByKeyAlg"
                    ></v-select>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-select
                      id="48eb665e"
                      v-model="simpleDocSignerDto.generateKey.keySize"
                      class="mx-3"
                      :items="simpleDocSignerDto.generateKey.keyAlg === keyAlgList[1] ? ecKeyNameList : rsaKeySizeList"
                      :label="$t('select') +' ' + $t('keySize')"
                      chips
                      :disabled="updateMode"
                    ></v-select>
                  </v-col>
                </v-row>
                <v-row v-if="keyImportPlatform">
                  <v-col cols="6" md="1">
                    <input
                      id="9a101b79"
                      ref="keyUpload"
                      style="display: none"
                      type="file"
                      @change="loadKeyFromFile($event)"
                    >
                    <v-btn class="green" @click="$refs.keyUpload.click()">
                      <v-icon left>mdi-upload</v-icon>
                      {{ !!simpleDocSignerDto.importKey.pem ? $t('replace') : $t('upload') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="6" md="4">
                    <v-text-field
                      id="b490e516"
                      v-model="simpleDocSignerDto.importKey.password"
                      :label="$t('keyPassword')"
                      type="password"
                      :rules="[rules.required]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row v-if="existingKeyPlatform">
                  <v-col>
                    <v-select
                      id="b88787f1"
                      v-model="simpleDocSignerDto.keyAlias"
                      class="mx-3"
                      :items="keyAliasList"
                      item-text="text"
                      item-value="value"
                      :label="$t('select') +' ' + $t('keyAlias')+`*`"
                      :rules="[rules.required]"
                      chips
                      :disabled="updateMode"
                    ></v-select>
                  </v-col>
                </v-row>

                <!-- setTimestampUrl -->
                <v-row align="center">
                  <v-col cols="6" md="2">
                    <v-switch
                      v-model="setTimestampUrl"
                      class="mx-3"
                      :label="$t('timestampUrl')"
                      @change="setTimestampUrlToData"
                    ></v-switch>
                  </v-col>
                  <v-col v-if="setTimestampUrl" cols="6" md="6">
                    <v-text-field
                      v-model="simpleDocSignerDto.timestampUrl"
                      :label="$t('timestampUrl')"
                      :counter="maxCharacters"
                      :rules="[rules.required, rules.minMax]"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- downloadCertReq -->
                <v-btn
                  v-if="updateMode"
                  id="be5346f3"
                  class="primary mx-2 my-2"
                  @click="downloadCertReqFile(simpleDocSignerDto.certReq.trim())"
                >
                  <v-icon left>mdi-download</v-icon>
                  {{ $t('download') +' '+ $t('certReq') }}
                </v-btn>

                <!-- loadSignerCertificate -->
                <div v-if="updateMode" class="mx-2">
                  <input
                    id="47114694"
                    ref="signerCertificateUpload"
                    style="display: none"
                    type="file"
                    accept=".pem, .cer"
                    @change="loadSignerCertificateFromFile($event)"
                  >
                  <v-btn class="green" @click="$refs.signerCertificateUpload.click()">
                    <v-icon left>mdi-upload</v-icon>
                    {{ !!simpleDocSignerDto.signerCert ? $t('replace') : $t('upload') }} {{ $t('signerCert') }}
                  </v-btn>
                </div>

                <!-- signerCert -->
                <v-row v-if="updateMode && !!simpleDocSignerDto.signerCert" id="942fca3d">
                  <v-col>
                    <v-card class="mx-2" raised>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle class="subtitle-2 text-left">
                            {{ $t('signerCert') }}
                          </v-list-item-subtitle>
                          <v-text-field
                            v-model="simpleDocSignerDto.signerCert.serialNumber"
                            :label="$t('serialNumber')"
                            :readonly="!!simpleDocSignerDto.signerCert"
                          ></v-text-field>
                          <v-text-field
                            v-model="simpleDocSignerDto.signerCert.issuer"
                            :label="$t('issuer')"
                            :readonly="!!simpleDocSignerDto.signerCert"
                          ></v-text-field>
                          <v-text-field
                            v-model="simpleDocSignerDto.signerCert.subject"
                            :label="$t('subject')"
                            :readonly="!!docSigner.signerCert"
                          ></v-text-field>
                          <v-row>
                            <v-col cols="6">
                              <v-text-field
                                :value="convertDate(simpleDocSignerDto.signerCert.notBefore)"
                                :label="$t('notBefore')"
                                :readonly="!!simpleDocSignerDto.signerCert"
                              ></v-text-field>
                            </v-col>
                            <v-col cols="6">
                              <v-text-field
                                :value="convertDate(simpleDocSignerDto.signerCert.notAfter)"
                                :label="$t('notAfter')"
                                :readonly="!!simpleDocSignerDto.signerCert"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-list-item-content>
                      </v-list-item>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- loadAdditionalCertificate -->
                <div v-if="updateMode" class="mx-2 my-2">
                  <input
                    id="3374c50b"
                    ref="additionalCertificateUpload"
                    style="display: none"
                    type="file"
                    accept=".pem, .cer"
                    @change="loadAdditionalCertificateFromFile($event)"
                  >
                  <v-btn class="green" @click="$refs.additionalCertificateUpload.click()">
                    <v-icon left>mdi-upload</v-icon>
                    {{ $t('upload') + ' ' + $t('additionalCert') }}
                  </v-btn>
                </div>

                <!-- additionalCert(s) -->
                <v-row v-if="updateMode" id="bd4e9a69">
                  <v-col v-for="(cert, index) in simpleDocSignerDto.additionalCerts" :key="index" cols="12">
                    <v-card class="mx-2" raised>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle class="subtitle-2 text-left">
                            {{ $t('additionalCert') + ' - ' + (index + 1) }}
                          </v-list-item-subtitle>
                          <v-text-field
                            v-model="simpleDocSignerDto.additionalCerts[index].serialNumber"
                            :label="$t('serialNumber')" :readonly="!!simpleDocSignerDto.additionalCerts[index]"
                          ></v-text-field>
                          <v-text-field
                            v-model="simpleDocSignerDto.additionalCerts[index].issuer"
                            :label="$t('issuer')"
                            :readonly="!!simpleDocSignerDto.additionalCerts[index]"
                          ></v-text-field>
                          <v-text-field
                            v-model="simpleDocSignerDto.additionalCerts[index].subject"
                            :label="$t('subject')"
                            :readonly="!!simpleDocSignerDto.additionalCerts[index]"
                          ></v-text-field>
                          <v-row>
                            <v-col cols="6">
                              <v-text-field
                                :value="convertDate(simpleDocSignerDto.additionalCerts[index].notBefore)"
                                :label="$t('notBefore')"
                                :readonly="!!simpleDocSignerDto.additionalCerts[index]"
                              ></v-text-field>
                            </v-col>
                            <v-col cols="6">
                              <v-text-field
                                :value="convertDate(simpleDocSignerDto.additionalCerts[index].notAfter)"
                                :label="$t('notAfter')"
                                :readonly="!!simpleDocSignerDto.additionalCerts[index]"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-list-item-content>
                      </v-list-item>
                      <v-card-actions>
                        <v-btn color="error" @click="removeAdditionalCert(index)">{{ $t('delete') }}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <div v-if="isMobileSignTimestampMode">
                <v-text-field
                  id="2635ed84"
                  v-model="simpleDocSignerDto.name"
                  :label="$t('name')+`*`"
                  :counter="maxCharacters"
                  :rules="[rules.required, rules.minMax]"
                  :disabled="updateMode"
                ></v-text-field>
                <v-row align="center">
                  <v-col cols="6" md="2">
                    <v-select
                      id="4d9a8f2a"
                      v-model="simpleDocSignerDto.sigParams.digestAlgorithm"
                      class="mx-3"
                      :items="digestAlgorithmList"
                      :label="$t('select') +' ' + $t('digestAlgorithm')"
                      chips
                    ></v-select>
                  </v-col>
                </v-row>

                <!-- setTimestampUrl -->
                <v-row align="center">
                  <v-col cols="6" md="2">
                    <v-switch
                      v-model="setTimestampUrl"
                      class="mx-3"
                      :label="$t('timestampUrl')"
                      @change="setTimestampUrlToData"
                    ></v-switch>
                  </v-col>
                  <v-col v-if="setTimestampUrl" cols="6" md="6">
                    <v-text-field
                      v-model="simpleDocSignerDto.timestampUrl"
                      :label="$t('timestampUrl')"
                      :counter="maxCharacters"
                      :rules="[rules.required, rules.minMax]"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>
            </v-form>
          </v-list-item-content>
        </v-list-item>
        <v-card-actions>
          <v-btn
            id="6540c94d"
            class="ma-2"
            color="primary"
            :loading="btnLoading"
            :disabled="!formValid || btnLoading"
            @click="createOrUpdateDocSigner"
          >
            {{ updateMode ? $t('update') : $t('create') }}
          </v-btn>
          <v-btn id="de56b0f5" class="ma-2" @click="back">{{ $t('back') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import Feature from '../../Feature'
import { mapGetters } from 'vuex'
import mobilSignMode from './MobilSignMode'
import keystores from '@/api/keystores'
import signers from '@/api/signers'
import certificates from '@/api/certificates'
import digitalSignatureService from '@/api/digitalSignatureService'
import FileSaver from 'file-saver'
import keyMethod from './KeyMethod'
import keyAlgorithm from './KeyAlgorithm'
import rsaKeySize from './RsaKeySize'
import ecKeyName from './EcKeyName'
import digestAlgorithm from './DigestAlgorithm'
import signatureForm from './SignatureForm'
import signatureProfile from './SignatureProfile'
import signatureExtension from './SignatureExtension'
import signaturePackaging from './SignaturePackaging'
import asicContainerType from './AsicContainerType'
import maskGenerationFunction from './MaskGenerationFunction'
import signerTextPosition from './SignerTextPosition'
import { showToastSuccess, showToastError } from '../../event-bus'

export default {
  name: 'CreateDocSignerOrUpdate',

  data() {
    return {
      formValid: true,
      isMobileSignMode: false,
      mobilSignMode: mobilSignMode[0],
      mobilSignModeList: mobilSignMode,
      setAdvancedJson: false,
      docSignerJsonData: null,
      maxCharacters: 255,
      keyStoreNameList: [],
      xmlTemplateList: [],
      xmlTemplateSuffix: ' ' + '(XML template)',
      keyMethodList: keyMethod,
      keyMethod: keyMethod[0],
      keyAliasList: [],
      keyAlgList: keyAlgorithm,
      rsaKeySizeList: rsaKeySize,
      ecKeyNameList: ecKeyName,
      digestAlgorithmList: digestAlgorithm,
      signatureForm: null,
      signatureFormList: signatureForm,
      signatureProfileList: signatureProfile,
      signatureExtensionList: signatureExtension,
      signatureLevelList: [],
      signaturePackagingList: signaturePackaging,
      asicContainerTypeList: asicContainerType,
      maskGenerationFunctionList: maskGenerationFunction,
      setTimestampUrl: false,
      setSignatureImageParameters: false,
      previewSignatureImage: null,
      setSignatureImageTextParameters: false,
      signerTextPositionList: signerTextPosition,
      setSignatureCodeSigningParameters: false,
      docSigner: {},
      simpleDocSignerDto: {
        generateKey: {
          keyAlg: keyAlgorithm[0],
          keySize: rsaKeySize[0]
        },
        importKey: {
          pem: null,
          password: null
        },
        keyAlias: null,
        sigParams: {
          digestAlgorithm: digestAlgorithm[1],
          signaturePackaging: signaturePackaging[0],
          asicContainerType: asicContainerType[0]
        }
      },
      signatureImageDto: {
        page: 1,
        xAxis: 0,
        yAxis: 0
      },
      signatureTextDto: {
        textColor: {
          red: 0,
          green: 0,
          blue: 0
        }
      },
      signatureCodeSigningDto: {
        format: null,
        isPGPDetached: false,
        gpgPublicKeyDN: '',
        gpgPublicKey: ''
      },
      keyGenerationPlatform: true,
      keyImportPlatform: false,
      existingKeyPlatform: false,
      btnLoading: false,
      rules: {
        required: v => (!!v || !isNaN(parseFloat(v))) || this.$t('validation.requiredField'),
        minMax: v => (v && v.length <= this.maxCharacters) || this.$t('validation.mustBeLessThan', { max: this.maxCharacters }),
        numberFromToValid: v => (v >= 0 && v <= 255) || this.$t('validation.numberFromToValid', { min: 0, max: 255 })
      }
    }
  },

  computed: {
    ...mapGetters(['hasFeatures']),
    isMOBILSIGN() {
      return this.hasFeatures(Feature.MOBILSIGN)
    },
    updateMode() {
      return this.$route.params.docSignerId != null
    },
    isCodeSigningFormat() {
      return (this.signatureForm != null) && this.signatureForm.includes('CodesSigning')
    },
    isCodeSigningGPGFormat() {
      return (this.signatureForm != null) && this.signatureForm.includes('PGP')
    },
    isMobileSignSignMode() {
      return this.isMobileSignMode && ((this.mobilSignMode != null) && this.mobilSignMode.includes(this.mobilSignModeList[0]))
    },
    isMobileSignTimestampMode() {
      return this.isMobileSignMode && ((this.mobilSignMode != null) && this.mobilSignMode.includes(this.mobilSignModeList[1]))
    },
    useDosszieMode() {
      return (this.signatureForm != null) && this.signatureForm.includes(this.signatureFormList[6])
    },
    useXmlTemplate() {
      return (this.signatureForm != null) && this.xmlTemplateList.includes(this.signatureForm)
    }
  },

  async mounted() {
    if (this.updateMode) {
      await this.getDocSigner()
    }
    await this.getKeyStoreNames()
    await this.getXmlTemplates()
  },

  methods: {
    async getDocSigner() {
      try {
        this.docSigner = await signers.getDocSigner(this.$route.params.docSignerId)
        this.setKeyMethodPlatformValues()
        this.setDefaultValuesByGetDocSigner()
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      }
    },

    async createOrUpdateDocSigner() {
      this.btnLoading = true

      if (!this.$refs.form.validate()) {
        return
      }

      // signatureImageParameters
      if (this.setSignatureImageParameters) {
        this.$set(this.simpleDocSignerDto.sigParams, 'imageParams', Object.assign({}, this.signatureImageDto))
        if (this.signatureImageDto.width === '') {
          this.$delete(this.docSigner.sigParams.imageParams, 'width')
        }
        if (this.signatureImageDto.height === '') {
          this.$delete(this.docSigner.sigParams.imageParams, 'height')
        }
      } else {
        this.$delete(this.simpleDocSignerDto.sigParams, 'imageParams')
      }

      // signatureImageTextParameters
      if (this.setSignatureImageTextParameters) {
        if (!this.setSignatureImageParameters) {
          this.$set(this.simpleDocSignerDto.sigParams, 'imageParams', {})
        }
        this.$set(this.simpleDocSignerDto.sigParams.imageParams, 'textParameters', Object.assign({}, this.signatureTextDto))
      } else {
        if (this.setSignatureImageParameters) {
          this.$delete(this.simpleDocSignerDto.sigParams.imageParams, 'textParameters')
        }
      }

      // signatureCodeSigningParameters
      if (this.isCodeSigningFormat) {
        let tempCodeSigningFormatAsString = this.signatureForm.replace('CodesSigning ', '')
        // convert user friendly code signer name to machine format
        if (tempCodeSigningFormatAsString === 'Microsoft Authenticode') {
          tempCodeSigningFormatAsString = 'MICROSOFTAUTHENTICODE'
        }
        this.signatureCodeSigningDto.format = tempCodeSigningFormatAsString

        this.$set(this.simpleDocSignerDto.sigParams, 'codeSigningParams', Object.assign({}, this.signatureCodeSigningDto))
        this.$delete(this.simpleDocSignerDto.sigParams, 'signatureLevel')
      } else {
        this.$delete(this.simpleDocSignerDto.sigParams, 'codeSigningParams')
      }

      this.simpleDocSignerDto = Object.assign({}, this.simpleDocSignerDto, {
        generateKey: this.keyMethod === this.keyMethodList[0] ? this.simpleDocSignerDto.generateKey : null,
        importKey: this.keyMethod === this.keyMethodList[1] ? this.simpleDocSignerDto.importKey : null,
        keyAlias: this.keyMethod === this.keyMethodList[2] ? this.simpleDocSignerDto.keyAlias : null
      })

      // mobilSignMode
      if (this.isMobileSignMode) {
        if (this.mobilSignMode === this.mobilSignModeList[1]) {
          this.$delete(this.simpleDocSignerDto, 'generateKey')
          this.$delete(this.simpleDocSignerDto, 'importKey')
          this.$delete(this.simpleDocSignerDto, 'keyAlias')
          this.$delete(this.simpleDocSignerDto.sigParams, 'signaturePackaging')
          this.$delete(this.simpleDocSignerDto.sigParams, 'asicContainerType')
        }

        this.$set(this.simpleDocSignerDto.sigParams, 'mobilSignMode', this.mobilSignMode)
      } else {
        this.$delete(this.simpleDocSignerDto.sigParams, 'mobilSignMode')
      }

      // dosszieMode
      if (this.useDosszieMode) {
        this.$delete(this.simpleDocSignerDto.sigParams, 'signaturePackaging')
        this.$delete(this.simpleDocSignerDto.sigParams, 'asicContainerType')

        this.$set(this.simpleDocSignerDto.sigParams, 'dosszieMode', this.useDosszieMode)
      }

      // xmlTemplate
      if (this.useXmlTemplate) {
        this.$delete(this.simpleDocSignerDto.sigParams, 'signaturePackaging')
        this.$delete(this.simpleDocSignerDto.sigParams, 'asicContainerType')

        this.$set(this.simpleDocSignerDto.sigParams, 'xmlTemplate', this.signatureForm.split(this.xmlTemplateSuffix)[0])
      } else {
        this.$delete(this.simpleDocSignerDto.sigParams, 'xmlTemplate')
      }

      this.docSigner = Object.assign({}, this.setAdvancedJson ? JSON.parse(this.docSignerJsonData) : this.simpleDocSignerDto)

      try {
        this.updateMode ? await signers.updateDocSigner(this.$route.params.docSignerId, this.docSigner) : await signers.createDocSigner(this.docSigner)
        showToastSuccess(this.updateMode ? this.$t('toastMessage.succesfullyUpdated') : this.$t('toastMessage.succesfullyCreated'))
        this.back()
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if (error.response != null) {
          switch (error.response.data.code) {
            case 'ALREADY_EXISTS':
              errorMsg = this.$t('toastMessage.alreadyExist')
              break
            case 'GENERAL':
              errorMsg = '(' + error.response.data.code + ')' + ' ' + error.response.data.message
          }
        }
        showToastError(errorMsg)
      } finally {
        this.btnLoading = false
      }
    },

    async getKeyStoreNames() {
      try {
        this.keyStoreNameList = await keystores.getKeyStoreNames()
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      }
    },

    async getXmlTemplates() {
      try {
        const xmlTemplates = await digitalSignatureService.getXmlTemplates()
        this.xmlTemplateList = xmlTemplates.map(e => e + this.xmlTemplateSuffix)
        if (this.xmlTemplateList.length !== 0) {
          this.signatureFormList.push(...this.xmlTemplateList)
        }
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      }
    },

    changeDefaultValuesByKeyAlg() {
      this.simpleDocSignerDto.generateKey.keySize = (this.simpleDocSignerDto.generateKey.keyAlg === this.keyAlgList[1] ? this.ecKeyNameList[0] : this.rsaKeySizeList[0])
    },

    changeVisibleKeyMethodPlatform() {
      switch (this.keyMethod) {
        case this.keyMethodList[0]:
          this.configurationOfVisiblePlatforms(true, false, false)
          break
        case this.keyMethodList[1]:
          this.configurationOfVisiblePlatforms(false, true, false)
          break
        case this.keyMethodList[2]:
          this.configurationOfVisiblePlatforms(false, false, true)
          break
      }
    },

    configurationOfVisiblePlatforms(keyGen, keyImp, keyExist) {
      this.keyGenerationPlatform = keyGen
      this.keyImportPlatform = keyImp
      this.existingKeyPlatform = keyExist
    },

    setKeyMethodPlatformValues() {
      const isGenerateKeyEmpty = Object.keys(this.docSigner.generateKey).length === 0
      this.keyGenerationPlatform = !isGenerateKeyEmpty
      if (isGenerateKeyEmpty) {
        this.keyAliasList = [{ text: this.docSigner.keyAlias, value: this.docSigner.keyAlias }]
        this.existingKeyPlatform = this.docSigner.keyAlias !== null
      }
    },

    creatingSignatureLevelListBySignatureForm() {
      let levels = []
      this.signatureExtensionList.forEach(extension => {
        this.signatureProfileList.forEach(profile => {
          levels.push(((this.useXmlTemplate || this.useDosszieMode) ? this.signatureFormList[0] : this.signatureForm) + profile + extension)
        })
      })
      this.signatureLevelList = Object.assign([], this.signatureLevelList, levels)
      if (!this.signatureLevelList.includes(this.simpleDocSignerDto.sigParams.signatureLevel)) {
        this.simpleDocSignerDto.sigParams.signatureLevel = this.signatureLevelList[0]
      }
    },

    loadSignatureImageFromFile(event) {
      const file = event.target.files[0]
      if (!(file.size > 1024 * 1024)) {
        const reader = new FileReader()
        reader.onload = event => this.addSignatureImage(file, event.target.result)
        reader.readAsDataURL(file)
      } else {
        showToastError(this.$t('toastMessage.selectedFileIsTooLarge'))
      }
      this.$refs.signatureImageUpload.value = null
    },

    addSignatureImage(file, imageDataUri) {
      let updatedDataUri = ''
      let splittedImageDataUri = imageDataUri.split(';')
      this.$set(this.simpleDocSignerDto, 'sigImgDataUrl', updatedDataUri.concat(splittedImageDataUri[0], ';filename=' + file.name + ';', splittedImageDataUri[1]))
    },

    setSignatureImageDataUrlToData() {
      this.$set(this.simpleDocSignerDto, 'sigImgDataUrl', this.setSignatureImageParameters ? this.simpleDocSignerDto.sigImgDataUrl : null)
    },

    setTimestampUrlToData() {
      this.$set(this.simpleDocSignerDto, 'timestampUrl', this.setTimestampUrl ? this.simpleDocSignerDto.timestampUrl : null)
    },

    loadDocSignerJsonDataFromFile(event) {
      const file = event.target.files[0]
      let reader = new FileReader()
      reader.onload = event => {
        this.docSignerJsonData = event.target.result
      }
      reader.readAsText(file)
      this.$refs.jsonUpload.value = null
    },

    async downloadCertReqFile(data) {
      await FileSaver.saveAs(new Blob([data], { type: 'application/x-pem-file' }), this.simpleDocSignerDto.name + '-certReq.pem')
    },

    loadSignerCertificateFromFile(event) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = event => this.addSignerCert(event.target.result)
      reader.readAsText(file)
      this.$refs.signerCertificateUpload.value = null
    },

    loadKeyFromFile(event) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = event => { this.simpleDocSignerDto.importKey.pem = event.target.result }
      reader.readAsText(file)
      this.$refs.keyUpload.value = null
    },

    async addSignerCert(value) {
      this.$set(this.simpleDocSignerDto, 'signerCert', Object.assign({}, await this.parseCertificate(value), { pem: value }))
    },

    loadAdditionalCertificateFromFile(event) {
      const file = event.target.files[0]
      const reader = new FileReader()
      reader.onload = event => this.addAdditionalCert(event.target.result)
      reader.readAsText(file)
      this.$refs.additionalCertificateUpload.value = null
    },

    async addAdditionalCert(value) {
      this.simpleDocSignerDto.additionalCerts.push(Object.assign({}, await this.parseCertificate(value), { pem: value }))
    },

    removeAdditionalCert(index) {
      this.simpleDocSignerDto.additionalCerts.splice(index, 1)
    },

    async parseCertificate(pem) {
      try {
        return await certificates.parseCertificate(pem)
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
        throw error
      }
    },

    setDefaultValuesByGetDocSigner() {
      this.simpleDocSignerDto = Object.assign({}, this.simpleDocSignerDto, this.docSigner)
      this.docSignerJsonData = JSON.stringify(this.docSigner, null, 1)

      // mobilSignMode
      const mobilSignMode = this.simpleDocSignerDto.sigParams.mobilSignMode
      const isMobilSignMode = mobilSignMode != null
      if (isMobilSignMode) {
        this.isMobileSignMode = true
        this.mobilSignMode = mobilSignMode
      }

      if (!isMobilSignMode) {
        // signatureFormAndSignatureLevel
        const isCodeSigningFormatSpecified = this.simpleDocSignerDto.sigParams.codeSigningParams != null
        if (isCodeSigningFormatSpecified) {
          if (this.simpleDocSignerDto.sigParams.codeSigningParams.format === 'JAR') { this.signatureForm = this.signatureFormList[3] }
          if (this.simpleDocSignerDto.sigParams.codeSigningParams.format === 'PGP') { this.signatureForm = this.signatureFormList[4] }
          if (this.simpleDocSignerDto.sigParams.codeSigningParams.format === 'MICROSOFTAUTHENTICODE') { this.signatureForm = this.signatureFormList[5] }
          this.signatureCodeSigningDto = this.simpleDocSignerDto.sigParams.codeSigningParams
          this.setGPGCertificatedPublicKey(this.signatureCodeSigningDto.gpgPublicKey)
          this.setGPGPublicKeyDN(this.signatureCodeSigningDto.gpgPublicKeyDN)
        } else {
          this.signatureForm = this.simpleDocSignerDto.sigParams.signatureLevel.split('_')[0]
          this.creatingSignatureLevelListBySignatureForm()
        }

        // asicContainerType
        if (this.simpleDocSignerDto.sigParams.asicContainerType === undefined) {
          this.simpleDocSignerDto.sigParams.asicContainerType = asicContainerType[0]
        }

        // signatureParameters
        if (this.simpleDocSignerDto.sigParams.imageParams) {
        // signatureImageParams
          this.setSignatureImageParameters = !!this.simpleDocSignerDto.sigParams.imageParams.page
          if (this.setSignatureImageParameters) {
            this.signatureImageDto = this.simpleDocSignerDto.sigParams.imageParams
          }

          // signatureTextParams
          this.setSignatureImageTextParameters = !!this.simpleDocSignerDto.sigParams.imageParams.textParameters
          if (this.setSignatureImageTextParameters) {
            this.signatureTextDto = this.simpleDocSignerDto.sigParams.imageParams.textParameters
          }
        }
      }

      // dosszieMode
      if (this.simpleDocSignerDto.sigParams.dosszieMode) {
        this.signatureForm = this.signatureFormList[6]
      }

      // xmlTemplate
      const xmlTemplate = this.simpleDocSignerDto.sigParams.xmlTemplate
      if (xmlTemplate != null) {
        this.signatureForm = xmlTemplate + this.xmlTemplateSuffix
      }

      // timestampUrl
      this.setTimestampUrl = !!this.simpleDocSignerDto.timestampUrl
    },

    async keyStoreNameChanged() {
      const entries = await keystores.getKeyStoreEntries(this.simpleDocSignerDto.keyStoreName)
      this.keyAliasList = entries.flatMap(e => {
        return { text: `${e.keyAlias} (${e.keyIdentifier})`, value: e.keyAlias }
      })
    },

    setGPGCertificatedPublicKey(publickey) {
      this.$set(this.signatureCodeSigningDto, 'gpgPublicKey', publickey)
    },

    setGPGPublicKeyDN(DN) {
      this.$set(this.signatureCodeSigningDto, 'gpgPublicKeyDN', DN)
    },

    async generateGPGCertificatedPublicKey() {
      const publickey = await signers.generateGPGCertificatedPublicKey(this.$route.params.docSignerId, this.signatureCodeSigningDto.gpgPublicKeyDN)
      this.setGPGCertificatedPublicKey(publickey)
    },

    back() {
      this.$router.go(-1)
    }
  }
}
</script>

<style>
.imagePreviewWrapper {
  width: 200px;
}
</style>
